<script setup lang="ts">
import type { ArticleSize } from '~/models/Content/ContentArticle'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { firstOf } from '~/utils/queryUtils'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { ColorTheme } from '~/models/Content/Color'
import { useMedia } from '~/composables/useMedia'
import useFormWriter from '~/composables/useFormWriter'
import { useColorTheme } from '~/composables/useColorTheme'
import { useAppColor } from '~/composables/useAppColor'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import TopBar from '~/components/utils/TopBar.vue'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import TextToSpeechButton from '~/components/media/TextToSpeechButton.vue'
import Image from '~/components/media/Image.vue'
import FormWriter from '~/components/formwriter/FormWriter.vue'
import ArticleTeacherGuide from '~/components/article/ArticleTeacherGuide.vue'
import ArticleNavigation from '~/components/article/ArticleNavigation.vue'

const props = defineProps<{ locationId: number }>()

const { t } = useI18n()
const { set: setAppColor, clear: clearAppColor } = useAppColor()
const { checkItemLicense } = useLicenseControlStore()
const { isTeacher, isAuthenticated } = storeToRefs(useAuthStore())
const { data, isLoadingFormData, isError } = useFormWriter(computed(() => props.locationId))

const { autoContentEvent } = useContentEvent()
autoContentEvent(data)

const teacherGuideOpen = ref(false)

const imageContentId = computed(() => [Number(data.value?.image?.destinationContentId ?? 0)])
const { data: bgMedia } = firstOf(useMedia(imageContentId))

const themeIdentifier = computed(() => data.value?.colorTheme?.identifier ?? ColorTheme.BlueOrange)
const { style } = useColorTheme(themeIdentifier)

const size = {
  medium: 'text-xl',
  large: 'text-2xl',
  xlarge: 'text-3xl'
} as const satisfies Record<ArticleSize, string>

const wrapper = {
  medium: 'lg:max-w-4xl',
  large: 'lg:max-w-5xl',
  xlarge: 'lg:max-w-6xl'
} as const satisfies Record<ArticleSize, string>

const showTeacherButton = computed(() => isTeacher.value && Number(data.value?.teacherGuide?.destinationContentId) > 0)

watch(data, () => {
  if (!data.value)
    return
  setTitle(data.value.title)
  checkItemLicense(data.value)

  if (themeIdentifier.value) {
    setAppColor(themeIdentifier.value)
  }
}, { immediate: true })

onMounted(() => {
  if (themeIdentifier.value) {
    setAppColor(themeIdentifier.value)
  }
})

onBeforeUnmount(() => {
  clearAppColor()
})
</script>

<template>
  <div class="bg-(--theme-5) text-lg text-(--theme-60)" :style>
    <NotFound v-if="isError" />
    <AuSpinner
      v-else-if="isLoadingFormData"
      center
    />
    <div
      v-else-if="data"
      class="flex min-h-screen flex-col"
    >
      <TopBar :item="data" />
      <div v-if="bgMedia" class="h-[50vh] flex-shrink-0">
        <Image
          class="!size-full min-h-full object-cover"
          :content="bgMedia"
        />
      </div>
      <div
        v-if="isAuthenticated"
        class="mx-auto flex w-full max-w-screen-md flex-1 flex-col items-center py-20 sm:px-8"
        :class="wrapper[data.size?.identifier ?? 'medium']"
      >
        <div class="w-full" :class="{ '-mt-32 md:-mt-40': bgMedia }">
          <div class="rounded-tl-xl rounded-tr-xl bg-(--theme-5)" :class="{ 'sm:pt-5 sm:pr-10 sm:pl-10 md:pt-10': bgMedia }">
            <div v-if="showTeacherButton || (data.textToSpeech && data.textToSpeechLanguage.identifier)" class="mb-4 flex items-center gap-3">
              <template v-if="showTeacherButton">
                <Button
                  color="teacher"
                  size="lg"
                  @click="teacherGuideOpen = true"
                >
                  {{ t('article.toTeacher') }}
                </Button>
                <ArticleTeacherGuide
                  :is-open="teacherGuideOpen"
                  :content-id="data.teacherGuide!.destinationContentId"
                  @close-guide="teacherGuideOpen = false"
                />
              </template>
              <TextToSpeechButton
                v-if="data.textToSpeech && data.textToSpeechLanguage.identifier"
                :item="data"
                :language-code="data.textToSpeechLanguage.identifier"
                selector=".au-formwriter"
              />
            </div>
            <FormWriter
              :item="data"
              class="au-formwriter"
              :class="size[data.size?.identifier ?? 'medium']"
            />
          </div>
        </div>
      </div>
    </div>
    <div id="audio-embed-outlet" class="sticky inset-x-0 bottom-0 bg-(--theme-60) p-4 text-(--theme-10) empty:hidden" />
  </div>
  <ArticleNavigation
    v-if="data"
    :item="data"
    :style
  />
</template>

<style scoped>
:deep(.cms-h3) {
  font-size: 1.4em;
}
</style>
